import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI, dashboardAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ChartComponent from '../../components/common/ChartComponent';
import StatCard from '../../components/common/StatCard';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [stats, setStats] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeacherData();
  }, [user]);

  const fetchTeacherData = async () => {
    try {
      setLoading(true);
      
      const [subjectsRes, statsRes] = await Promise.all([
        teacherAPI.getAssignedSubjects(),
        // dashboardAPI.getTeacherStats(),
      ]);

      setAssignedSubjects(subjectsRes.data);
      // setStats(statsRes.data);

      // Mock recent activities
      // setRecentActivities([
      //   { id: 1, action: 'Updated Math scores for Grade 10A', time: '2 hours ago', type: 'update' },
      //   { id: 2, action: 'Added comments for Physics project', time: '1 day ago', type: 'comment' },
      //   { id: 3, action: 'Submitted term assessment', time: '2 days ago', type: 'submit' },
      //   { id: 4, action: 'Assigned new project to students', time: '3 days ago', type: 'assignment' },
      // ]);

    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Welcome back, {user?.name}!</h1>
            <p className="opacity-90 mt-1">Here's your teaching overview</p>
          </div>
          <div className="hidden md:block">
            <i className="fas fa-chalkboard-teacher text-5xl opacity-20"></i>
          </div>
        </div>
      </div>

    

      {/* Statistics Cards */}


      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assigned Subjects */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">My Assigned Subjects</h3>
            <span className="text-sm text-gray-500">{assignedSubjects.length} subjects</span>
          </div>
          
          <div className="space-y-4">
            {assignedSubjects.length > 0 ? (
              assignedSubjects.map((subject) => (
                <div key={subject.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{subject.subject_name}</h4>
                      <p className="text-sm text-gray-500">Class: {subject.class_name}</p>
                    </div>
                    <div className="text-right">
                      {/* <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">24</div>
                          <div className="text-xs text-gray-500">Students</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">85%</div>
                          <div className="text-xs text-gray-500">Avg Score</div>
                        </div>
                      </div> */}
                    </div>
                  </div>
                  
                  <div className="mt-4 flex space-x-2">
                    <a
                      href={`/teacher/capture-assessment?subject=${subject.subject_id}&class=${subject.class_id}`}
                      className="btn-primary text-sm py-2 px-4 flex-1 text-center"
                         style={{ textDecoration: "none" }}
                    >
                      <i className="fas fa-edit mr-2"></i> Enter Scores
                    </a>
                    <a
                      // href={`/teacher/student-progress?subject=${subject.subject_id}`}
                      href={`#`}
                      className="btn-secondary text-sm py-2 px-4 flex-1 text-center"
                         style={{ textDecoration: "none" }}
                    >
                      <i className="fas fa-chart-line mr-2"></i> View Progress
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <i className="fas fa-book text-3xl text-gray-300 mb-3"></i>
                <p className="text-gray-500">No subjects assigned yet</p>
                <p className="text-sm text-gray-400 mt-1">Contact administrator for subject assignments</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities & Upcoming */}
        <div className="space-y-6">
          {/* Recent Activities */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Activities</h3>
              <button className="text-sm text-primary-600 hover:text-primary-700">
                View all
              </button>
            </div>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.type === 'update' ? 'bg-blue-100 text-blue-600' :
                    activity.type === 'comment' ? 'bg-green-100 text-green-600' :
                    activity.type === 'submit' ? 'bg-purple-100 text-purple-600' :
                    'bg-yellow-100 text-yellow-600'
                  }`}>
                    <i className={`fas ${
                      activity.type === 'update' ? 'fa-edit' :
                      activity.type === 'comment' ? 'fa-comment' :
                      activity.type === 'submit' ? 'fa-check' :
                      'fa-tasks'
                    } text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Upcoming Deadlines</h3>
            <div className="space-y-4">
              {/* {[
                { subject: 'Mathematics', task: 'Term Assessment', deadline: 'Tomorrow', class: '10A' },
                // { subject: 'Physics', task: 'Project Submission', deadline: 'Dec 15', class: '10B' },
                // { subject: 'Chemistry', task: 'Lab Reports', deadline: 'Dec 18', class: '11A' },
              ].map((deadline, index) => (
                <div key={index} className="border-l-4 border-yellow-500 pl-4 py-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{deadline.task}</h4>
                      <p className="text-sm text-gray-500">{deadline.subject} • {deadline.class}</p>
                    </div>
                    <span className="text-sm font-medium text-yellow-600">{deadline.deadline}</span>
                  </div>
                </div>
              ))} */}
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default TeacherDashboard;